import React, { useState, useContext } from "react";
import { AdvancedDragModal, ADModalBody } from "./AdvancedDragModal";
import { GlassButton, GlassButtonDropdown, GlassButtonRow, GlassInfoButton, OnMapButton } from "src/components/GlassButtons";
import { ViewButtonIcon, ViewButtonCheck, ViewButtonText, ViewButtonPopDown, ViewButtonDropdown, ViewButtonTextInput, ViewButtonCaret, GlassFoldUdBox } from "src/propertyInfoTemplates/PropertyComponents";
import { GlassColorBox, GlassTypeAheadReordering, GlassColorPalette, GlassInputGroup, GlassInputGroupTitle, GlassTextInputTransformer, GICButton, GlassInputControl, GlassInputControlLabel } from "src/components/GlassInput";
import { GlassCheckbox, MenuButton, LargeIconBtn, ExtendedMenuButton, GlassCard, GlassContainer, GlassList, LegendGrid, PaddedContainer, VLine } from 'src/components/MitGlassComponents';
import { TagChangerTypeAhead } from 'src/common/components/InputFields';
import ActionSelector from 'src/components/ActionSelector';
import { SelectLayer } from 'src/components/CommonInputs';
import { ColorBoxEditableV2 } from "src/components/ComponentUtils";
import { Typeahead } from 'react-bootstrap-typeahead';
import { Localization } from '@viamap/viamap2-common';
import { BsHouseFill, BsArrowUpRightSquareFill } from "react-icons/bs";
import { TSwitch } from "src/components/TSwitch";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import Form from 'react-bootstrap/Form';

function DemoSection(props: { title: React.ReactNode; children?: React.ReactNode; innerAlign?: any; marginTop?: number }) {
   const { title, children, innerAlign = 'center', marginTop = 0 } = props;
   return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginTop: marginTop }}>
         <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
         <div style={{ display: 'flex', gap: 12, alignItems: innerAlign, justifyContent: 'center', width: '100%' }}>
            {children}
         </div>
      </div>
   );
}

export function DebugModal(props: { onClose?: () => void }) {
   const [open, setOpen] = useState(true);
   const [showNS, setShowNS] = useState(false);
   const [checkA, setCheckA] = useState(false);
   const [textCount, setTextCount] = useState(0);
   const [textDisabled, setTextDisabled] = useState(false);
   const [gbWrap, setGbWrap] = useState(false);
   const [gbAdjust, setGbAdjust] = useState<"start" | "center" | "end">("start");
   const [popOpen, setPopOpen] = useState(false);
   const [popSelection, setPopSelection] = useState<string | null>(null);
   const [viewDropSelection, setViewDropSelection] = useState<string | null>(null);
   const [infoMsg, setInfoMsg] = useState<string | null>(null);
   const [menuOpen, setMenuOpen] = useState<boolean>(false);
   const [onmapPlacement, setOnmapPlacement] = useState<"top" | "bottom" | "left" | "right">("bottom");
   const [onmapTitle, setOnmapTitle] = useState<string>("Map tooltip");
   const [onmapShowTitle, setOnmapShowTitle] = useState<boolean>(true);
   const [nativeSelect, setNativeSelect] = useState<string>('one');
   const [rbSelect, setRbSelect] = useState<string>('alpha');
   const [textInputValue, setTextInputValue] = useState<string>('hello');
   const [typeOptions] = useState<string[]>(['Option A', 'Option B', 'Option C', 'Extra']);
   const [typeValue, setTypeValue] = useState<string | undefined>(undefined);
   const [reorderOptions] = useState<{ label: string, value: string }[]>([
      { label: 'Alpha', value: 'alpha' },
      { label: 'Bravo', value: 'bravo' },
      { label: 'Charlie', value: 'charlie' },
      { label: 'Delta', value: 'delta' }
   ]);
   const [reorderSelected, setReorderSelected] = useState<{ label: string, value: string }[]>([{ label: 'Alpha', value: 'alpha' }, { label: 'Charlie', value: 'charlie' }]);
   const [tagOptions] = useState<string[]>(['urgent', 'review', 'external', 'internal']);
   const [tagValue, setTagValue] = useState<{ bfeNr: number, tags: string[] }>({ bfeNr: 0, tags: ['urgent'] });
   const [paletteColors] = useState<string[]>(['#2b80ff', '#ff6b6b', '#ffd166', '#06d6a0']);
   const [paletteSelected, setPaletteSelected] = useState<string>(paletteColors[0]);
   const [formName, setFormName] = useState<string>('Example');
   const [formType, setFormType] = useState<string>('A');
   const [demoActiveTool, setDemoActiveTool] = useState<string>('none');
   const [showGroupTitle, setShowGroupTitle] = useState<boolean>(true);
   const [foldOpen, setFoldOpen] = useState<boolean>(true);
   const [caretOpen, setCaretOpen] = useState<boolean>(false);
   const [selectedColor, setSelectedColor] = useState<string>('#2b80ff');
   const [editableCommittedColor, setEditableCommittedColor] = useState<string>('#2b80ff');
   // sample layers for SelectLayer demo
   const [demoLayers] = useState<any[]>([
      { layerId: 101, datasetname: 'Parks', geoJson: null },
      { layerId: 102, datasetname: 'Lakes', geoJson: null },
      { layerId: 103, datasetname: 'Protected Areas', geoJson: null }
   ]);
   const [selectedLayerId, setSelectedLayerId] = useState<number | undefined>(undefined);
   const baseCtx = useContext(ApplicationStateContext);
   const previewCtx = {
      ...baseCtx,
      hasAccessToFeature: (f: any) => {
         if (f === Feature.ThemeNewSec) return showNS;
         return baseCtx.hasAccessToFeature(f);
      }
   } as any;
   if (!open) return null;
   const handleClose = () => {
      setOpen(false);
      if (props.onClose) props.onClose();
   }
   return (
      <AdvancedDragModal
         PosDefault={{ width: "640px", height: "460px", top: "80px", left: "50%", transform: "translateX(-50%)" }}
         saveKey="DebugModal"
         onClose={handleClose}
         title={"Debug Modal"}
         allowUserMiniMize={true}
         allowFullscreen={true}
      >
         <ADModalBody>
            <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 32, alignItems: "stretch", minHeight: 0, width: "100%", height: "100%", boxSizing: "border-box", overflowY: "auto" }}>
               {/* Buttons section (simplified) */}
               <DemoSection title={"GlassButton (demo)"}>
                  <GlassButton title="Example glass button" onClick={() => { alert("GlassButton clicked") }}>Example Button</GlassButton>
               </DemoSection>

               {/* moved GlassButtonDropdown & GlassButtonRow to be adjacent to GlassButton (grouped) */}
               <DemoSection title={"GlassButtonDropdown (demo)"}>
                  <GlassButtonDropdown options={[{ text: "First", value: 1 }, { text: "Second", value: 2 }, { text: "Third", value: 3 }]} onSelect={(v) => alert("Selected: " + v)}>
                     Dropdown
                  </GlassButtonDropdown>
               </DemoSection>

               <DemoSection title={"GlassButtonRow (demo)"}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                     <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <input type="checkbox" checked={gbWrap} onChange={(e) => setGbWrap(e.target.checked)} /> Wrap
                     </label>
                     <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        Align:
                        <select value={gbAdjust} onChange={(e) => setGbAdjust(e.target.value as any)} style={{ padding: 6, borderRadius: 6 }}>
                           <option value="start">start</option>
                           <option value="center">center</option>
                           <option value="end">end</option>
                        </select>
                     </label>
                  </div>
                  <GlassButtonRow wrap={gbWrap} adjust={gbAdjust}>
                     <GlassButton onClick={() => alert('One')}>One</GlassButton>
                     <GlassButton onClick={() => alert('Two')}>Two</GlassButton>
                     <GlassButton onClick={() => alert('Three')}>Three</GlassButton>
                     <GlassButton onClick={() => alert('Four')}>Four</GlassButton>
                     <GlassButton onClick={() => alert('Five')}>Five</GlassButton>
                     <GlassButton onClick={() => alert('Six')}>Six</GlassButton>
                  </GlassButtonRow>
               </DemoSection>

               {/* PaddedContainer demo section */}
               <DemoSection title={"PaddedContainer (demo)"} innerAlign={'flex-start'} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 220, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <PaddedContainer>
                        <div style={{ fontWeight: 600 }}>Padded content</div>
                        <div style={{ marginTop: 6 }}>This content is wrapped by PaddedContainer which should add internal spacing consistently.</div>
                     </PaddedContainer>
                  </div>
                  <div style={{ minWidth: 160 }}><span style={{ color: 'rgba(0,0,0,0.4)' }}>Adds internal padding</span></div>
               </DemoSection>

               {/* VLine demo section */}
               <DemoSection title={"VLine (demo)"} innerAlign={'center'} marginTop={8}>
                  <div style={{ minWidth: 160 }}><span style={{ color: 'rgba(0,0,0,0.4)' }}>Vertical separator</span></div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%', justifyContent: 'center' }}>
                     <div style={{ padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, width: 220, boxSizing: 'border-box', justifyContent: 'center' }}>
                        <div style={{ marginBottom: 6 }}>Top</div>
                        <div style={{ width: '100%' }}>
                           <VLine />
                        </div>
                        <div style={{ marginTop: 6 }}>Bottom</div>
                     </div>
                  </div>
               </DemoSection>

               {/* TSwitch demo section */}
               <DemoSection title={"TSwitch (demo)"} innerAlign={'center'}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                     <div style={{ padding: 8, borderRadius: 6, border: "1px solid rgba(0,0,0,0.08)", background: "var(--ownerShipBackground, #fff)" }}>
                        <ApplicationStateContext.Provider value={previewCtx}>
                           <TSwitch DF={<span style={{ padding: 6 }}>Default</span>} NS={<span style={{ padding: 6 }}>NewSec</span>} />
                        </ApplicationStateContext.Provider>
                     </div>
                  </div>

                  {/* A simple local preview toggle so devs can see both states */}
                  <TSwitchPreview showNS={showNS} setShowNS={setShowNS} />
               </DemoSection>

               {/* GlassButtonDropdown demo section (moved to top) */}
               {/* ViewButtonText demo section */}
               <DemoSection title={"ViewButtonText (demo)"}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                     <ViewButtonText onClick={() => setTextCount((n) => n + 1)} disabled={textDisabled}>
                        Clicked {textCount} times
                     </ViewButtonText>
                     <button onClick={() => setTextDisabled((s) => !s)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.12)', background: 'transparent' }}>{textDisabled ? 'Enable' : 'Disable'}</button>
                  </div>
               </DemoSection>

               {/* -- moved ViewButton blocks (grouped) -- */}
               {/* ViewButtonIcon demo section */}
               <DemoSection title={"ViewButtonIcon (demo)"}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                     <ViewButtonIcon title={"House"} onClick={() => alert("House clicked")}><BsHouseFill /></ViewButtonIcon>
                     <ViewButtonIcon title={"Open"} onClick={() => alert("Open clicked")}><BsArrowUpRightSquareFill /></ViewButtonIcon>
                  </div>
               </DemoSection>

               {/* ViewButtonPopDown demo section */}
               <DemoSection title={"ViewButtonPopDown (demo)"}>
                  <ViewButtonPopDown title={"Tooltip text"} open={popOpen} popdown={
                     <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <button onClick={() => { setPopSelection('A'); setPopOpen(false); }}>Option A</button>
                        <button onClick={() => { setPopSelection('B'); setPopOpen(false); }}>Option B</button>
                        <button onClick={() => { setPopSelection('C'); setPopOpen(false); }}>Option C</button>
                     </div>
                  } onClick={() => setPopOpen((s) => !s)}>
                     PopDown {popSelection ? '(' + popSelection + ')' : ''}
                  </ViewButtonPopDown>
               </DemoSection>

               {/* ViewButtonDropdown demo section */}
               <DemoSection title={"ViewButtonDropdown (demo)"} marginTop={8}>
                  <ViewButtonDropdown options={[{ text: 'Alpha', value: 'A' }, { text: 'Beta', value: 'B' }, { text: 'Gamma', value: 'C' }]} onSelect={(v) => setViewDropSelection(v)}>
                     Choose: {viewDropSelection ?? 'none'}
                  </ViewButtonDropdown>
               </DemoSection>

               {/* ViewButtonTextInput demo section */}
               <DemoSection title={"ViewButtonTextInput (demo)"} marginTop={8}>
                  <ViewButtonTextInput value={textInputValue} onChange={(v) => setTextInputValue(v)} />
                  <div style={{ minWidth: 160 }}>Value: {textInputValue}</div>
               </DemoSection>

               {/* ViewButtonCheck demo section */}
               <DemoSection title={"ViewButtonCheck (demo)"}>
                  <ViewButtonCheck checked={checkA} onCheck={() => setCheckA((s) => !s)} >{checkA ? 'Checked' : 'Unchecked'}</ViewButtonCheck>
               </DemoSection>

               {/* ViewButtonCaret demo section */}
               <DemoSection title={"ViewButtonCaret (demo)"}>
                  <ViewButtonCaret isOpen={caretOpen} onClick={() => setCaretOpen((s) => !s)}>{caretOpen ? 'Hide' : 'Show'}</ViewButtonCaret>
               </DemoSection>

               {/* Selects demo section */}
               <DemoSection title={"Selects (demo)"} marginTop={8}>
                  <select value={nativeSelect} onChange={(e) => setNativeSelect(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
                     <option value="one">One</option>
                     <option value="two">Two</option>
                     <option value="three">Three</option>
                  </select>

                  <Form.Select value={rbSelect} onChange={(e) => setRbSelect(e.target.value)} style={{ width: 160 }}>
                     <option value="alpha">Alpha</option>
                     <option value="beta">Beta</option>
                     <option value="gamma">Gamma</option>
                  </Form.Select>

                  <div style={{ minWidth: 140 }}>Native: {nativeSelect} · RB: {rbSelect}</div>
               </DemoSection>

               {/* TypeAheadSingle demo section */}
               <DemoSection title={"TypeAheadSingle (demo)"} marginTop={8}>
                  <div style={{ minWidth: 320 }}>
                     <Typeahead
                        id="debug-typeahead"
                        multiple={false}
                        options={typeOptions.map(v => ({ label: v, value: v }))}
                        labelKey={'label'}
                        placeholder={Localization.getText('please select')}
                        onChange={(selected: any[]) => setTypeValue(selected?.[selected.length - 1]?.value || "")}
                        selected={typeValue ? [{ label: typeValue, value: typeValue }] : []}
                     />
                  </div>
                  <div style={{ minWidth: 160 }}>Selected: {typeValue ?? '<none>'}</div>
               </DemoSection>

               {/* GlassTypeAheadReordering demo section */}
               <DemoSection title={"GlassTypeAheadReordering (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 260, maxWidth: 320 }}>
                     <GlassTypeAheadReordering
                        layerId={0}
                        id="debug-reorder"
                        options={reorderOptions}
                        selected={reorderSelected}
                        placeholder={Localization.getText('please select')}
                        emptyLabel={Localization.getText('no options')}
                        onChange={(a) => setReorderSelected(a)}
                        settingComponent={<div style={{ padding: 8, fontSize: 12 }}>{reorderSelected.length} selected</div>}
                     />
                  </div>
                  <div style={{ minWidth: 160 }}>Selected: {reorderSelected.map(s => s.label).join(', ') || '<none>'}</div>
               </DemoSection>

               {/* TagChangerTypeAhead demo section */}
               <DemoSection title={"TagChangerTypeAhead (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320 }}>
                     <TagChangerTypeAhead
                        id="debug-tags"
                        value={tagValue}
                        options={tagOptions}
                        callbacks={{
                           remT: (bfeNr: number, tag: string) => setTagValue((s) => ({ ...s, tags: s.tags.filter((t) => t !== tag) })),
                           addT: (bfeNr: number, tag: string) => setTagValue((s) => ({ ...s, tags: [...s.tags, tag] })),
                        }}
                     />
                  </div>
                  <div style={{ minWidth: 160 }}>Tags: {tagValue.tags.join(', ') || '<none>'}</div>
               </DemoSection>

               {/* GlassColorPalette demo section */}
               <DemoSection title={"GlassColorPalette (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <GlassColorPalette
                        colorSet={paletteColors}
                        defaultColor={paletteColors[0]}
                        getter={() => paletteSelected}
                        onChange={(c) => setPaletteSelected(c)}
                     />
                  </div>

                  <div style={{ minWidth: 160 }}>Selected: <span style={{ padding: '2px 8px', borderRadius: 6, background: paletteSelected, color: paletteSelected === '#ffffff' ? '#000' : '#fff' }}>{paletteSelected}</span></div>
               </DemoSection>

               {/* GlassColorBox demo section */}
               <DemoSection title={"GlassColorBox (demo)"} marginTop={8}>
                  {['#2b80ff', '#ff6b6b', '#ffd166', '#06d6a0', '#8338ec', '#000000', '#ffffff'].map((c) => (
                     <GlassColorBox key={c} color={c} selected={c.toLowerCase() === selectedColor.toLowerCase()} onClick={() => setSelectedColor(c)} />
                  ))}
                  <div style={{ minWidth: 160 }}>Selected: <span style={{ padding: '2px 8px', borderRadius: 6, background: selectedColor, color: selectedColor === '#ffffff' ? '#000' : '#fff' }}>{selectedColor}</span></div>
               </DemoSection>

               {/* ColorBoxEditableV2 demo section */}
               <DemoSection title={"ColorBoxEditableV2 (demo)"} marginTop={8}>
                  <div style={{ paddingLeft: 4 }}>
                     <ColorBoxEditableV2
                        color={editableCommittedColor}
                        onCommit={(c) => setEditableCommittedColor(c)}
                     />
                  </div>
                  <div style={{ minWidth: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Committed: <span style={{ padding: '2px 8px', borderRadius: 6, background: editableCommittedColor, color: editableCommittedColor === '#ffffff' ? '#000' : '#fff', marginLeft: 8 }}>{editableCommittedColor}</span></div>
               </DemoSection>

               {/* GlassInputGroup demo section */}
               <DemoSection title={"GlassInputGroup (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320 }}>
                     <GlassInputGroup onSubmit={() => alert(`Submitted: ${formName} / ${formType}`)} onEscape={() => alert('Escape pressed')}>
                        <GlassTextInputTransformer label={'Name'}>
                           <input value={formName} onChange={(e) => setFormName(e.target.value)} style={{ padding: 6, borderRadius: 6 }} />
                        </GlassTextInputTransformer>
                        <GlassTextInputTransformer label={'Type'}>
                           <select value={formType} onChange={(e) => setFormType(e.target.value)} style={{ padding: 6, borderRadius: 6 }}>
                              <option value={'A'}>A</option>
                              <option value={'B'}>B</option>
                           </select>
                        </GlassTextInputTransformer>
                     </GlassInputGroup>
                  </div>
                  <div style={{ minWidth: 160 }}>Preview: {formName} · {formType}</div>
               </DemoSection>

               {/* GlassInputGroupTitle demo section */}
               <DemoSection title={"GlassInputGroupTitle (demo)"} marginTop={4}>
                  <div style={{ flex: 1, minWidth: 320, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <label style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                        <input type="checkbox" checked={showGroupTitle} onChange={(e) => setShowGroupTitle(e.target.checked)} /> Show title
                     </label>
                     <GlassInputGroup>
                        {showGroupTitle && <GlassInputGroupTitle>Form title spans both columns</GlassInputGroupTitle>}
                        <GlassTextInputTransformer label={'Field A'}>
                           <input style={{ padding: 6, borderRadius: 6 }} defaultValue={"Value A"} />
                        </GlassTextInputTransformer>
                        <GlassTextInputTransformer label={'Field B'}>
                           <input style={{ padding: 6, borderRadius: 6 }} defaultValue={"Value B"} />
                        </GlassTextInputTransformer>
                     </GlassInputGroup>
                  </div>
               </DemoSection>

               {/* GlassFoldUdBox demo section */}
               <DemoSection title={"GlassFoldUdBox (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <label style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                        <input type="checkbox" checked={foldOpen} onChange={(e) => setFoldOpen(e.target.checked)} /> Open
                     </label>
                     <GlassFoldUdBox title={<div style={{ fontWeight: 600 }}>Foldable title</div>} foldetUd={foldOpen}>
                        <div style={{ padding: 8 }}>
                           Hidden content area
                           <br />
                           <span style={{ marginTop: 8, display: 'block' }}>Put any JSX here (lists, images, etc.)</span>
                        </div>
                     </GlassFoldUdBox>
                  </div>
                  <div style={{ minWidth: 160 }}>Click the checkbox to toggle</div>
               </DemoSection>

               {/* GlassCheckbox demo section */}
               <DemoSection title={"GlassCheckbox (demo)"} marginTop={8}>
                  <GlassCheckbox checked={checkA} onClick={(e, v) => setCheckA(v)}>Example checkbox</GlassCheckbox>
                  <div style={{ minWidth: 160 }}>{checkA ? 'Checked' : 'Unchecked'}</div>
               </DemoSection>

               {/* MenuButton demo section */}
               <DemoSection title={"MenuButton (demo)"} marginTop={8}>
                  <div style={{ position: 'relative' }}>
                     <MenuButton toolTip={"This is a MenuButton tooltip"} onClick={() => setMenuOpen((s) => !s)}>☰</MenuButton>
                     {menuOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderRadius: 6, zIndex: 9999 }}>
                           <button style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: 'transparent', textAlign: 'left' }} onClick={() => { setInfoMsg('Option 1 selected'); setMenuOpen(false); }}>Option 1</button>
                           <button style={{ display: 'block', width: '100%', padding: '8px 12px', border: 'none', background: 'transparent', textAlign: 'left' }} onClick={() => { setInfoMsg('Option 2 selected'); setMenuOpen(false); }}>Option 2</button>
                        </div>
                     )}
                  </div>
                  <div style={{ minWidth: 160 }}>{infoMsg ?? <span style={{ color: 'rgba(0,0,0,0.4)' }}>No message</span>}</div>
               </DemoSection>

               {/* LargeIconBtn demo section */}
               <DemoSection title={"LargeIconBtn (demo)"} marginTop={8}>
                  <LargeIconBtn title={"Large icon tooltip"} onClick={() => setInfoMsg('LargeIconBtn clicked')}>🔍</LargeIconBtn>
                  <div style={{ minWidth: 160 }}>{infoMsg ?? <span style={{ color: 'rgba(0,0,0,0.4)' }}>No message</span>}</div>
               </DemoSection>

               {/* ExtendedMenuButton demo section */}
               <DemoSection title={"ExtendedMenuButton (demo)"} marginTop={8}>
                  <ExtendedMenuButton toolTip={"Extended tooltip"} onClick={() => setInfoMsg('ExtendedMenuButton clicked')}>⋯</ExtendedMenuButton>
                  <div style={{ minWidth: 160 }}>{infoMsg ?? <span style={{ color: 'rgba(0,0,0,0.4)' }}>No message</span>}</div>
               </DemoSection>

               {/* GlassCard demo section */}
               <DemoSection title={"GlassCard (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)', maxWidth: 360 }}>
                     <GlassCard title={<div style={{ fontWeight: 600, whiteSpace: 'normal' }}>Demo card title</div>} onClick={() => setInfoMsg('GlassCard clicked')}>
                        <div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>Card body content — this is a GlassCard demo showing title and body. The text should wrap so the card grows vertically instead of stretching horizontally.</div>
                        <div style={{ marginTop: 8 }}>
                           <button onClick={(e) => { e.stopPropagation(); setInfoMsg('GlassCard inner action clicked'); }} style={{ padding: '6px 8px', borderRadius: 6 }}>Action</button>
                        </div>
                     </GlassCard>
                  </div>
                  <div style={{ minWidth: 160 }}>{infoMsg ?? <span style={{ color: 'rgba(0,0,0,0.4)' }}>No message</span>}</div>
               </DemoSection>

               {/* GlassContainer demo section */}
               <DemoSection title={"GlassContainer (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)', maxWidth: 360 }}>
                     <GlassContainer title={<div style={{ fontWeight: 600 }}>Collapsible container</div>}>
                        <div style={{ padding: 8 }}>
                           This is content inside GlassContainer. Click the header to toggle.
                        </div>
                     </GlassContainer>
                  </div>
                  <div style={{ minWidth: 160 }}><span style={{ color: 'rgba(0,0,0,0.4)' }}>Header toggles content</span></div>
               </DemoSection>

               {/* GlassList demo section */}
               <DemoSection title={"GlassList (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)', maxWidth: 360 }}>
                     <GlassList title={<div style={{ fontWeight: 600 }}>List title</div>}>
                        <div style={{ padding: 8 }}>
                           • Item one
                           <br />
                           • Item two
                           <br />
                           • Item three
                        </div>
                     </GlassList>
                  </div>
                  <div style={{ minWidth: 160 }}><span style={{ color: 'rgba(0,0,0,0.4)' }}>Simple list preview</span></div>
               </DemoSection>

               {/* LegendGrid demo section (moved out of DemoSection to avoid nested grid issues) */}
               <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8, alignItems: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>LegendGrid (demo)</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', width: '100%' }}>
                     <div style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                        <LegendGrid>
                           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                              {['One', 'Two', 'Three', 'Four', 'Five', 'Six'].map((t) => <div key={t} style={{ padding: 6, borderRadius: 6, background: 'rgba(0,0,0,0.04)' }}>{t}</div>)}
                           </div>
                        </LegendGrid>
                     </div>
                     <div style={{ minWidth: 160 }}><span style={{ color: 'rgba(0,0,0,0.4)' }}>Simple legend grid</span></div>
                  </div>
               </div>

               {/* GICButton demo section (wrapped in GlassInputControl for realistic appearance) */}
               <DemoSection title={"GICButton (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <GlassInputControl>
                        <GICButton title={"This is a tooltip"} onClick={() => { setInfoMsg('GICButton clicked (demo)'); alert('GICButton clicked (demo)'); }}>GIC</GICButton>
                     </GlassInputControl>
                  </div>
                  <div style={{ minWidth: 160 }}>Hover the button to show tooltip</div>
               </DemoSection>


               {/* GlassInputControl demo section */}
               <DemoSection title={"GlassInputControl (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320, display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                     <div style={{ whiteSpace: 'nowrap', marginRight: 4 }}>
                        <GlassInputControlLabel>Example</GlassInputControlLabel>
                     </div>
                     <div style={{ flex: '0 0 auto', minWidth: 160, maxWidth: 420 }}>
                        <GlassInputControl>
                           <input value={textInputValue} onChange={(e) => setTextInputValue(e.target.value)} style={{ padding: 6, borderRadius: 6, width: '100%' }} />
                        </GlassInputControl>
                     </div>
                  </div>
                  <div style={{ minWidth: 160 }}>Value: {textInputValue}</div>
               </DemoSection>

               {/* SelectLayer demo section (moved out so it doesn't interrupt the input row) */}
               <DemoSection title={"SelectLayer (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 320, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <SelectLayer
                        selectedLayerId={selectedLayerId as any}
                        availableLayers={demoLayers}
                        title={"Demo layers"}
                        onSelect={(id) => setSelectedLayerId(id)}
                     />
                  </div>
                  <div style={{ minWidth: 160 }}>Selected: {selectedLayerId ?? '<none>'}</div>
               </DemoSection>

               {/* ActionSelector demo section */}
               <DemoSection title={"ActionSelector (demo)"} marginTop={8}>
                  <div style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)' }}>
                     <ActionSelector activeTool={demoActiveTool} setActiveTool={setDemoActiveTool} onCapture={() => setInfoMsg('Capture pressed (demo)')} />
                  </div>
                  <div style={{ minWidth: 160 }}>Selected: {demoActiveTool}</div>
               </DemoSection>

               {/* GlassInfoButton demo section */}
               <DemoSection title={"GlassInfoButton (demo)"} marginTop={8}>
                  <GlassInfoButton onClick={() => setInfoMsg((m) => m ? null : 'Info clicked!')}>i</GlassInfoButton>
                  <div style={{ minWidth: 120 }}>{infoMsg ?? <span style={{ color: 'rgba(0,0,0,0.4)' }}>No message</span>}</div>
               </DemoSection>

               {/* OnMapButton demo section */}
               <DemoSection title={"OnMapButton (demo)"} marginTop={8}>
                  <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                     Placement:
                     <select value={onmapPlacement} onChange={(e) => setOnmapPlacement(e.target.value as any)} style={{ padding: 6, borderRadius: 6 }}>
                        <option value="bottom">bottom</option>
                        <option value="top">top</option>
                        <option value="left">left</option>
                        <option value="right">right</option>
                     </select>
                  </label>
                  <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                     Title:
                     <input value={onmapTitle} onChange={(e) => setOnmapTitle(e.target.value)} style={{ padding: 6, borderRadius: 6 }} />
                  </label>
                  <label style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                     <input type="checkbox" checked={onmapShowTitle} onChange={(e) => setOnmapShowTitle(e.target.checked)} /> Show
                  </label>
                  <OnMapButton {...{ "title-placement": onmapPlacement }} title={onmapShowTitle ? onmapTitle : undefined} onClick={() => alert('OnMapButton clicked')}>Map</OnMapButton>
               </DemoSection>
            </div>
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function TSwitchPreview({ showNS, setShowNS }: { showNS: boolean, setShowNS: (v: boolean | ((s: boolean) => boolean)) => void }) {
   return (
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 8 }}>
         <button onClick={() => setShowNS((s: boolean) => !s)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.12)', background: 'transparent' }}>
            Toggle preview
         </button>
         <div style={{ padding: 6, borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)', minWidth: 80, textAlign: 'center' }}>
            {showNS ? <span>NS (preview)</span> : <span>DF (preview)</span>}
         </div>
      </div>
   );
}
